import { useCallback } from 'react';
import Vec2 from 'vec2';

import { useSelectCutoutLabor } from '~/api/easy-estimator-api/cutout-labor-api/hooks/useCutoutLabor';
import { FAUX_SHAPE_INDEX } from '~/core/constants';
import { drawingTool } from '~/core/engine/DrawingTool';
import { ShapeType } from '~/core/enums';
import { isCookingApplianceProduct, subtractVectors } from '~/core/helpers';
import {
  ApplianceModel,
  ApplianceType,
  AppliancesAllowedOnShapes,
  DrawingAppliance,
  DrawingShape,
} from '~/core/models';
import { ApplianceQtyType } from '~/core/types/appliances';
import {
  findClosestValidPosition,
  getApproximateApplianceOutline,
  isEqApplianceId,
  isRange,
  makeApplianceName,
  removeApplianceFromShape,
} from '~/core/utils';
import {
  ensureMutableArray,
  forceAssignProperty,
} from '~/core/utils/objectMutability.utils';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import {
  decreaseCookingAppliance,
  syncCookingApplianceOnShape,
} from '~/redux/slices/cookingAppliance';
import {
  decreaseApplianceCutouts,
  decreaseCustomCutouts,
} from '~/redux/slices/cutout';
import {
  setActiveDrawingObject,
  updateNeedRedrawCanvas,
} from '~/redux/slices/drawingShape';
import { selectFabricCanvas } from '~/redux/slices/fabric';
import { resetSelectedSink } from '~/redux/slices/sink';
import { useDrawingShapeContext } from '~contexts';

/**
 * Result of an appliance move operation
 */
export interface ApplianceMoveResult {
  success: boolean;
  originalPosition?: Vec2;
  appliance?: DrawingAppliance;
  sourceShape?: DrawingShape;
}

/**
 * Context for processing an appliance move.
 */
interface ApplianceMoveContext {
  appliance: DrawingAppliance;
  applianceIndex: number;
  sourceShape: DrawingShape;
  originalPosition: Vec2;
  potentialCenterPoint: Vec2;
}

/**
 * Type alias for the moveApplianceToAnotherShape callback.
 */
type MoveApplianceToAnotherShapeFn = (
  currentShape: DrawingShape,
  potentialCenterPoint: Vec2,
  applianceIndex: number,
  shapesParam: DrawingShape[]
) => boolean;

/*
 * Type for found appliance in shape
 */
type FindApplianceResult = {
  appliance: DrawingAppliance;
  index: number;
  shape: DrawingShape;
};

// ============================================================================
// APPLIANCE MOVE HELPERS
// ============================================================================

/**
 * Finds an appliance across all shapes by its prefixed ID.
 * Returns the appliance, its index, and source shape if found.
 */
export const findApplianceInShapes = (
  applianceId: string,
  shapes: DrawingShape[]
) =>
  shapes.reduce<FindApplianceResult | undefined>((result, shape) => {
    const index = result
      ? -1
      : shape.appliances.findIndex(isEqApplianceId(applianceId));

    return index > -1
      ? { appliance: shape.appliances[index], index, shape }
      : result;
  }, undefined);

/**
 * Reverts appliance position to original coordinates.
 * Creates new Vec2 to ensure canvas detects the change.
 */
const revertAppliancePosition = (
  appliance: DrawingAppliance,
  originalPosition: Vec2
): void => {
  appliance.centerPoint = new Vec2(originalPosition.x, originalPosition.y);
};

/**
 * Attempts to move appliance within its current shape.
 * Returns true if successful, false if no valid position.
 * Reverts position if no valid position found.
 */
const tryMoveWithinSameShape = (context: ApplianceMoveContext): boolean => {
  const { appliance, sourceShape, potentialCenterPoint, originalPosition } =
    context;

  const otherAppliances = sourceShape.appliances.filter(
    (a) => a.id !== appliance.id
  );

  const validPosition = findClosestValidPosition(
    potentialCenterPoint,
    appliance,
    sourceShape,
    otherAppliances
  );

  if (validPosition) {
    appliance.centerPoint = validPosition;

    return true;
  }

  revertAppliancePosition(appliance, originalPosition);

  return false;
};

/**
 * Attempts to move appliance to another shape or white space.
 * Returns true if successful, false otherwise.
 * Reverts position if cross-shape move fails.
 */
const tryMoveToOtherShape = (
  context: ApplianceMoveContext,
  drawingShapesParam: DrawingShape[],
  moveApplianceToAnotherShape: MoveApplianceToAnotherShapeFn
): boolean => {
  const {
    sourceShape,
    potentialCenterPoint,
    applianceIndex,
    appliance,
    originalPosition,
  } = context;

  const success = moveApplianceToAnotherShape(
    sourceShape,
    potentialCenterPoint,
    applianceIndex,
    drawingShapesParam
  );

  if (!success) {
    revertAppliancePosition(appliance, originalPosition);
  }

  return success;
};

// ============================================================================
// MAIN HOOK
// ============================================================================

const useAppliance = () => {
  const dispatch = useAppDispatch();
  const canvas = useAppSelector(selectFabricCanvas);
  const {
    getters: { drawingShapes, activeDrawingApplianceId },
    handlers: { updateDrawingShapes },
  } = useDrawingShapeContext();

  const { cooktopCutout } = useSelectCutoutLabor();

  const transferApplianceBetweenShapes = (
    xferringAppliance: DrawingAppliance,
    sourceShape: DrawingShape,
    targetShape: DrawingShape
  ): void => {
    // delete the appliance from the source shape
    const foundIndex = sourceShape.appliances.findIndex(
      isEqApplianceId(makeApplianceName(xferringAppliance))
    );

    if (foundIndex > -1) {
      const mutableSourceArray = ensureMutableArray(sourceShape.appliances);

      if (mutableSourceArray !== sourceShape.appliances) {
        forceAssignProperty(sourceShape, 'appliances', mutableSourceArray);
      }

      mutableSourceArray.splice(foundIndex, 1);
    }

    const mutableTargetArray = ensureMutableArray(targetShape.appliances);

    if (mutableTargetArray !== targetShape.appliances) {
      forceAssignProperty(targetShape, 'appliances', mutableTargetArray);
    }

    // add the appliance to the target shape
    mutableTargetArray.push(xferringAppliance);
  };

  const moveApplianceToAnotherShape = useCallback(
    (
      currentShape: DrawingShape,
      potentialCenterPoint: Vec2,
      applianceIndex: number,
      shapesParam: DrawingShape[] // FIX: Accept shapes as parameter to avoid stale closure
    ) => {
      let applianceIsAccepted = false;
      let functionComplete = false;

      const movingAppliance = currentShape.appliances[applianceIndex];
      const applianceType = movingAppliance.applianceModel.applianceType;

      const isApplianceAllowedOnShapes =
        AppliancesAllowedOnShapes.indexOf(applianceType) > -1;

      // Check non-FAUX shapes first, excluding the source shape
      // FIX: Use shapesParam instead of drawingShapes from closure
      // FIX: Exclude currentShape to prevent trying to place appliance on its own shape
      shapesParam
        .filter(
          (shape) =>
            shape.type !== ShapeType.FAUX_SHAPE && shape.id !== currentShape.id
        )
        .forEach((shape) => {
          if (functionComplete) {
            return;
          } // Early exit if already found a valid target

          const isApplianceOnCountertop = drawingTool.isApplianceOnCountertop(
            potentialCenterPoint,
            movingAppliance,
            shape,
            false
          );

          if (isApplianceOnCountertop && isApplianceAllowedOnShapes) {
            // Check for collision with other appliances on target shape
            const otherAppliances = shape.appliances.filter(
              (a) => a.id !== movingAppliance.id
            );
            const validPosition = findClosestValidPosition(
              potentialCenterPoint,
              movingAppliance,
              shape,
              otherAppliances
            );

            if (validPosition) {
              applianceIsAccepted = true;
              movingAppliance.centerPoint = validPosition;
              transferApplianceBetweenShapes(
                movingAppliance,
                currentShape,
                shape
              );
              functionComplete = true;
            }
          }
        });

      // Check if appliance can go to white space (only RANGE and REFRIGERATOR)
      const canGoOutside =
        applianceType === ApplianceType.RANGE ||
        applianceType === ApplianceType.REFRIGERATOR;

      if (!applianceIsAccepted && canGoOutside) {
        applianceIsAccepted = true;
        movingAppliance.centerPoint = potentialCenterPoint;
        transferApplianceBetweenShapes(
          movingAppliance,
          currentShape,
          shapesParam[FAUX_SHAPE_INDEX] // FIX: Use shapesParam instead of drawingShapes
        );
        functionComplete = true;
      }

      return functionComplete;
    },
    [] // FIX: Remove drawingShapes from dependencies - now passed as parameter
  );

  /**
   * Updates an appliance's position based on a translation vector.
   * Handles movement within same shape or to another shape.
   * Returns result with success status and context for animations.
   */
  const updateApplianceByTranslation = (
    moveVec: Vec2,
    movingApplianceId: string,
    drawingShapesParam: DrawingShape[]
  ): ApplianceMoveResult => {
    // Find the appliance across all shapes
    const found = findApplianceInShapes(movingApplianceId, drawingShapesParam);

    if (!found) {
      return { success: false };
    }

    const { appliance, index: applianceIndex, shape: sourceShape } = found;

    // Store original position for potential reversion
    const originalPosition = new Vec2(
      appliance.centerPoint.x,
      appliance.centerPoint.y
    );

    const potentialCenterPoint = subtractVectors(
      appliance.centerPoint,
      moveVec
    );

    // Build move context
    const context: ApplianceMoveContext = {
      appliance,
      applianceIndex,
      sourceShape,
      originalPosition,
      potentialCenterPoint,
    };

    // Check if appliance is still on current shape
    const isOnCurrentShape = drawingTool.isApplianceOnCountertop(
      potentialCenterPoint,
      appliance,
      sourceShape,
      false
    );

    // Try appropriate move strategy
    const success = isOnCurrentShape
      ? tryMoveWithinSameShape(context)
      : tryMoveToOtherShape(
          context,
          drawingShapesParam,
          moveApplianceToAnotherShape
        );

    return {
      success,
      originalPosition,
      appliance,
      sourceShape,
    };
  };

  const getMutableAppliancesArray = (
    shape: DrawingShape
  ): DrawingAppliance[] => {
    const mutableAppliancesArray = ensureMutableArray(shape.appliances);

    if (mutableAppliancesArray !== shape.appliances) {
      forceAssignProperty(shape, 'appliances', mutableAppliancesArray);
    }

    return mutableAppliancesArray;
  };

  const removeAppliancesFromArray = (
    appliances: DrawingAppliance[],
    productId: ApplianceModel['productId'],
    maxAmount?: number
  ) => {
    let removed = 0;

    for (let i = appliances.length - 1; i >= 0; i--) {
      if (appliances[i].applianceModel.productId === productId) {
        appliances.splice(i, 1);
        removed++;
        if (maxAmount && removed >= maxAmount) {
          break;
        }
      }
    }
  };

  const deleteAppliancesById = (
    productId: ApplianceModel['productId'],
    maxAmount?: number
  ): void => {
    drawingShapes.forEach((shape: DrawingShape) => {
      const mutableAppliancesArray = getMutableAppliancesArray(shape);

      removeAppliancesFromArray(mutableAppliancesArray, productId, maxAmount);
    });

    updateDrawingShapes([...drawingShapes]);
    dispatch(updateNeedRedrawCanvas(true));
  };

  const deleteAllAppliancesByType = (applianceTypes: string[]): void => {
    drawingShapes.forEach((shape: DrawingShape) => {
      const mutableAppliancesArray = getMutableAppliancesArray(shape);

      for (
        let applIndex = mutableAppliancesArray.length - 1;
        applIndex >= 0;
        applIndex--
      ) {
        if (
          applianceTypes.indexOf(
            mutableAppliancesArray[applIndex].applianceModel.applianceType
          ) > -1
        ) {
          mutableAppliancesArray.splice(applIndex, 1);
        }
      }
    });
    updateDrawingShapes([...drawingShapes]);
    drawingTool.clearCanvas({ canvas });
    dispatch(updateNeedRedrawCanvas(true));
  };

  const applianceTypesInDrawingArea = (applianceTypes: string[]): boolean =>
    drawingShapes.some((shape: DrawingShape) =>
      shape.appliances.some(
        (appliance) =>
          applianceTypes.indexOf(appliance.applianceModel.applianceType) > -1
      )
    );

  const handleCutoutsAfterApplianceRemoval = useCallback(
    (applianceToBeRemoved: DrawingAppliance) => {
      const { productId, applianceType } = applianceToBeRemoved.applianceModel;
      const cutoutsOnShape = [
        ApplianceType.ELECTRICAL_CUTOUT,
        ApplianceType.FAUCET_CUTOUT,
      ];
      const cookingAppliancesOnShape = [
        ApplianceType.COOKTOP,
        ApplianceType.RANGE,
      ];

      if (cutoutsOnShape.includes(applianceType)) {
        dispatch(
          decreaseCustomCutouts({
            cutoutId: productId,
            qtyType: ApplianceQtyType.onShape,
          })
        );
      } else if (cookingAppliancesOnShape.includes(applianceType)) {
        const { type, consumerDesc } = applianceToBeRemoved.applianceModel;

        const isExplicitlyRange =
          isRange({ applianceModel: applianceToBeRemoved.applianceModel }) ||
          (type && isRange({ applianceType: type })) ||
          (consumerDesc && isRange({ applianceType: consumerDesc }));

        const selectedType = isExplicitlyRange
          ? 'selectedRangeApplianceProduct'
          : 'selectedCooktopApplianceProduct';

        // This ensures drag-and-drop cooking appliances cutouts products are removed too
        if (cooktopCutout && !isExplicitlyRange) {
          dispatch(
            decreaseApplianceCutouts({
              cutoutId: cooktopCutout.productId,
              applianceId: applianceToBeRemoved.applianceModel.productId,
            })
          );
        }

        dispatch(
          decreaseCookingAppliance({
            qtyType: ApplianceQtyType.onShape,
            applianceId: productId,
            selectedType,
          })
        );
      }
    },
    [dispatch, cooktopCutout]
  );

  const getApplianceAndShape = useCallback(
    (
      applianceId?: string
    ): { appliance?: DrawingAppliance; applianceShape?: DrawingShape } => {
      let appliance: DrawingAppliance | undefined;
      let applianceShape: DrawingShape | undefined;
      const applianceToFindId = applianceId ?? activeDrawingApplianceId;

      if (applianceToFindId) {
        applianceShape = drawingShapes.find((shape) => {
          appliance = shape.appliances.find(isEqApplianceId(applianceToFindId));

          return !!appliance;
        });
      }

      return {
        appliance,
        applianceShape,
      };
    },
    [drawingShapes, activeDrawingApplianceId]
  );

  const syncCookingAppliancesCount = useCallback(() => {
    const cookingAppliancesCount: Record<
      string,
      {
        type: ApplianceType;
        count: number;
      }
    > = {};

    drawingShapes.forEach((shape) => {
      shape.appliances.forEach((appliance) => {
        const { productId, applianceType } = appliance.applianceModel;

        if (isCookingApplianceProduct(appliance.applianceModel)) {
          if (!cookingAppliancesCount[productId]) {
            cookingAppliancesCount[productId] = {
              type: applianceType,
              count: 0,
            };
          }
          cookingAppliancesCount[productId].count += 1;
        }
      });
    });

    Object.entries(cookingAppliancesCount).forEach(
      ([productId, { type, count }]) => {
        const selectedType = isRange({ applianceType: type })
          ? 'selectedRangeApplianceProduct'
          : 'selectedCooktopApplianceProduct';

        dispatch(
          syncCookingApplianceOnShape({
            selectedType,
            applianceId: Number(productId),
            count,
          })
        );
      }
    );
  }, [dispatch, drawingShapes]);

  const removeApplianceFromDrawingArea = useCallback(
    (applianceId?: string) => {
      const { applianceShape, appliance } = getApplianceAndShape(applianceId);

      if (!appliance || !applianceShape) {
        return;
      }

      const applianceProductId = appliance.applianceModel.productId;

      handleCutoutsAfterApplianceRemoval(appliance);

      dispatch(resetSelectedSink());
      const activeShapeId =
        applianceShape.type === ShapeType.FAUX_SHAPE
          ? (drawingShapes.find((shape) => shape.type !== ShapeType.FAUX_SHAPE)
              ?.id ?? applianceShape.id)
          : applianceShape.id;

      dispatch(
        setActiveDrawingObject({
          type: 'shape',
          id: activeShapeId,
        })
      );

      const updatedShape = removeApplianceFromShape(applianceShape, appliance);
      const updatedDrawingShapes = drawingShapes.map((shape) =>
        shape.id === applianceShape.id ? updatedShape : shape
      );

      updateDrawingShapes(updatedDrawingShapes);
      dispatch(updateNeedRedrawCanvas(true));

      let applianceCount = 0;

      updatedDrawingShapes.forEach((shape) => {
        shape.appliances.forEach((a) => {
          if (a.applianceModel.productId === applianceProductId) {
            applianceCount++;
          }
        });
      });

      if (applianceCount > 0) {
        syncCookingAppliancesCount();
      }
    },
    [
      dispatch,
      drawingShapes,
      getApplianceAndShape,
      handleCutoutsAfterApplianceRemoval,
      updateDrawingShapes,
      syncCookingAppliancesCount,
    ]
  );

  return {
    updateApplianceByTranslation,
    deleteAllAppliancesByType,
    deleteAppliancesById,
    applianceTypesInDrawingArea,
    getApproximateApplianceOutline,
    removeApplianceFromDrawingArea,
    syncCookingAppliancesCount,
    transferApplianceBetweenShapes,
  };
};

export default useAppliance;
